import React, { useState } from 'react';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import SaveIcon from '@mui/icons-material/Save';
import { LoadingButton } from '@mui/lab';
import {
  useCreateEmailTemplateMutation,
  useUpdateEmailTemplateMutation,
} from '../../redux/features/notificationTemplate/notificationTemplate.apiSlice';
import { EmailNotificationTemplate, EmailTemplateSnapshot } from '../../types/notificationTemplate';
import { NotificationTemplateHistory } from './NotificationTemplateHistory';
import {
  describeKeyRename,
  describeProblem,
  describeSaveError,
  isValidKey,
  KEY_MAX_LENGTH,
  SENDGRID_TEMPLATE_ID_PATTERN,
} from './templateCopy';

type FormValues = { key: string; sendgridTemplateId: string; description: string; isActive: boolean };

const toFormValues = (template: EmailNotificationTemplate | null): FormValues => ({
  key: template?.key || '',
  sendgridTemplateId: template?.sendgridTemplateId || '',
  description: template?.description || '',
  isActive: template?.isActive ?? false,
});

/** What the server stores for these values: trimmed text, and a blank description as null. */
const toSnapshot = (values: FormValues): Required<Omit<EmailTemplateSnapshot, 'category'>> => ({
  key: values.key.trim(),
  sendgridTemplateId: values.sendgridTemplateId.trim(),
  description: values.description.trim() || null,
  isActive: values.isActive,
});

interface EmailTemplateDialogProps {
  open: boolean;
  /** The template to edit, or null to create one. */
  template: EmailNotificationTemplate | null;
  /** Existing templates, so a key that already has one is caught before the round trip. */
  templates: EmailNotificationTemplate[];
  canUpdate: boolean;
  onClose: () => void;
}

/**
 * Which SendGrid template an email key sends (MAD-4965). The email itself, in both languages, is edited in SendGrid;
 * it receives the code's params plus the user's names (nameEn, nameAr) and language.
 */
export function EmailTemplateDialog({ open, template, templates, canUpdate, onClose }: EmailTemplateDialogProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [createTemplate, { isLoading: isCreating }] = useCreateEmailTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateEmailTemplateMutation();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const creating = !template;
  const editable = creating || canUpdate;

  const { values, handleChange, setFieldValue, submitForm, resetForm } = useFormik<FormValues>({
    initialValues: toFormValues(template),
    enableReinitialize: true,
    onSubmit: async (formValues) => {
      const next = toSnapshot(formValues);

      setSaveErrors([]);

      try {
        if (creating) {
          await createTemplate(next).unwrap();
        } else {
          const saved = toSnapshot(toFormValues(template));
          const changes = (Object.keys(next) as (keyof typeof next)[]).reduce(
            (acc, field) => (next[field] === saved[field] ? acc : { ...acc, [field]: next[field] }),
            {} as Partial<EmailTemplateSnapshot>,
          );

          await updateTemplate({ id: template.id, ...changes }).unwrap();
        }

        enqueueSnackbar(creating ? 'Email template created.' : 'Email template saved.', { variant: 'success' });
        resetForm();
        onClose();
      } catch (err) {
        setSaveErrors(describeSaveError(err, 'The email template could not be saved.'));
      }
    },
  });

  const key = values.key.trim();
  const sendgridTemplateId = values.sendgridTemplateId.trim();
  // A new key, typed for a new template or a rename, gets the checks the notifier makes.
  const keyChanged = !creating && key !== template.key;
  const checkKey = creating || keyChanged;
  const taken = checkKey && templates.some((existing) => existing.id !== template?.id && existing.key === key);
  const keyError = checkKey && key && !isValidKey(key) ? describeProblem({ code: 'INVALID_KEY', key }) : '';
  const idError =
    sendgridTemplateId && !SENDGRID_TEMPLATE_ID_PATTERN.test(sendgridTemplateId)
      ? describeProblem({ code: 'INVALID_SENDGRID_TEMPLATE_ID', sendgridTemplateId })
      : '';
  const saved = toSnapshot(toFormValues(template));
  const edited = toSnapshot(values);
  const isDirty = (Object.keys(edited) as (keyof typeof edited)[]).some((field) => edited[field] !== saved[field]);
  // Switching off is never refused, so a template with a wrong id can always be stopped.
  const onlySwitchedOff =
    !creating && !keyChanged && !edited.isActive && edited.sendgridTemplateId === saved.sendgridTemplateId;
  const keyBlocked = !key || Boolean(keyError) || taken;
  const blocked = creating
    ? keyBlocked || !sendgridTemplateId || Boolean(idError)
    : !isDirty || (keyChanged && keyBlocked) || (!onlySwitchedOff && (!sendgridTemplateId || Boolean(idError)));

  const handleClose = () => {
    setSaveErrors([]);
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <span>{creating ? 'New email template' : 'Email template'}</span>
          {!creating && (
            <Typography component="code" sx={{ fontFamily: 'monospace' }}>
              {template.key}
            </Typography>
          )}
          {template?.category && <Chip size="small" label={template.category} />}
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 1 }}>
          The email is designed and worded in SendGrid. Here you choose which SendGrid template a key sends. It receives
          the values the code sends, plus <code>nameEn</code>, <code>nameAr</code> and <code>language</code>.
        </Alert>
        <TextField
          margin="normal"
          fullWidth
          required
          id="email-template-key"
          name="key"
          label="Key"
          placeholder="funds.card_payment_confirmed"
          value={values.key}
          onChange={handleChange}
          disabled={!editable}
          inputProps={{ maxLength: KEY_MAX_LENGTH, dir: 'ltr' }}
          error={Boolean(keyError) || taken}
          helperText={
            taken
              ? 'Another email template already has this key.'
              : keyError || 'What the code sends. Agree it with the developers first.'
          }
        />
        {keyChanged && !keyBlocked && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {describeKeyRename(template.key, key)}
          </Alert>
        )}
        <TextField
          margin="normal"
          fullWidth
          required
          id="email-template-sendgrid-id"
          name="sendgridTemplateId"
          label="SendGrid template id"
          placeholder="d-0123456789abcdef0123456789abcdef"
          value={values.sendgridTemplateId}
          onChange={handleChange}
          disabled={!editable}
          inputProps={{ maxLength: 64, dir: 'ltr' }}
          error={Boolean(idError)}
          helperText={idError || 'As shown in SendGrid under Dynamic Templates.'}
        />
        <TextField
          margin="normal"
          fullWidth
          id="email-template-description"
          name="description"
          label="Description"
          value={values.description}
          onChange={handleChange}
          disabled={!editable}
          inputProps={{ maxLength: 255 }}
          helperText="What the email is, since its content is not shown here."
        />
        <FormControlLabel
          control={
            <Switch
              checked={values.isActive}
              onChange={(event) => setFieldValue('isActive', event.target.checked)}
              disabled={!editable}
            />
          }
          label={values.isActive ? 'Active: emails with this key are sent' : 'Inactive: nothing is sent'}
        />
        {saveErrors.length > 0 && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {saveErrors.map((message) => (
              <div key={message}>{message}</div>
            ))}
          </Alert>
        )}
        {template && <NotificationTemplateHistory channel="EMAIL" templateId={template.id} />}
      </DialogContent>
      <DialogActions>
        {editable && (
          <LoadingButton
            type="button"
            color="primary"
            variant="contained"
            endIcon={<SaveIcon />}
            loading={isCreating || isUpdating}
            loadingPosition="end"
            disabled={blocked}
            onClick={submitForm}
          >
            {creating ? 'Create' : 'Save'}
          </LoadingButton>
        )}
        <Button color="inherit" variant="outlined" onClick={handleClose}>
          {editable ? 'Cancel' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
